import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { TaskRecord } from "@nexus/contracts";

import { personaOpportunityHubKeys } from "@/lib/queries/persona-opportunity-hub.queries";
import { invalidatePersonaOpportunitySets } from "@/lib/queries/persona-opportunity-sets.queries";
import {
  consumePersonaSetGenerationCancelled,
  detectFinishedPersonaSetGenerationIds,
} from "../lib/persona-set-generation-task";

/**
 * When a generate-set task hits progress 1 (or leaves the active list), refetch
 * list + detail immediately instead of waiting for the GENERATING poll tick.
 * The hub's opportunity index was read while the set was still empty and has no
 * poll of its own, so it is refetched too.
 */
export function useInvalidatePersonaSetsOnGenerationTaskEnd(
  tasks: readonly TaskRecord[],
  tenantId: string | null | undefined,
): void {
  const queryClient = useQueryClient();
  const inProgressRef = React.useRef<Set<string>>(new Set());
  const trackedTenantRef = React.useRef(tenantId);

  // Drop in-flight ids on tenant switch so a filtered-out task is not treated as a finish.
  if (trackedTenantRef.current !== tenantId) {
    trackedTenantRef.current = tenantId;
    inProgressRef.current = new Set();
  }

  React.useEffect(() => {
    const scopedTasks = tenantId
      ? tasks.filter((task) => task.tenantId === tenantId)
      : [];
    const { inProgress, finishedIds } = detectFinishedPersonaSetGenerationIds(
      inProgressRef.current,
      scopedTasks,
    );
    inProgressRef.current = inProgress;
    if (finishedIds.length === 0) return;
    // A dismissed cancel is not a finished generation. The set is still live
    // until the worker soft-deletes it, so hub counts wait for that.
    const finishedGenerations = finishedIds.filter(
      (correlationId) => !consumePersonaSetGenerationCancelled(correlationId),
    );
    invalidatePersonaOpportunitySets(queryClient, tenantId);
    if (finishedGenerations.length === 0) return;
    void queryClient.invalidateQueries({ queryKey: personaOpportunityHubKeys.all });
  }, [queryClient, tasks, tenantId]);
}
